/**
 * The portal's own password rule for the Sleepio Canopy screens.
 *
 * Twelve characters, plus a lower-case letter, an upper-case letter, a digit and a
 * symbol. Decided 2026-08-14; it ratifies what the Supabase project already enforces
 * rather than changing it (TDD-sleepio-password-sign-in, "The password rule").
 *
 * Deliberately NOT `PASSWORD_VALIDATION_REGEX` in `constants.ts` — that one asks for
 * 8-24 characters and three classes, and belongs to the older enrollment-backed reset
 * form that no Sleepio path reaches. Also deliberately not the mobile app's
 * `passwordValidation.ts` (8 characters), which is another repository's business.
 *
 * Everything member-facing derives from the constant below. The helper line shown
 * before the member types interpolates `PASSWORD_MIN_LENGTH`, and so does the
 * too-short error. That is the point: a test asserts the copy against this constant
 * instead of against a second hard-coded string, so the next change to the number
 * cannot quietly leave the words behind.
 */

export const PASSWORD_MIN_LENGTH = 12;

/**
 * The symbol set Supabase itself counts as a symbol. Matching it exactly matters:
 * a character we accepted but the server did not would let a member satisfy the
 * screen in front of them and still be refused, which is the failure this rule
 * exists to remove.
 */
export const PASSWORD_SYMBOLS = '!@#$%^&*()_+-=[]{};\'\\:"|<>?,./`~';

export type PasswordRuleId = 'length' | 'lowercase' | 'uppercase' | 'digit' | 'symbol';

interface PasswordRule {
  id: PasswordRuleId;
  /** i18n key for the message shown when this rule is the one that failed. */
  errorKey: string;
  satisfiedBy: (value: string) => boolean;
}

/**
 * Ordered. The first unsatisfied rule is the one the member is told about, and the
 * order is why a short password naming every class still reports its length rather
 * than a class it actually has.
 */
export const PASSWORD_RULES: readonly PasswordRule[] = [
  {
    id: 'length',
    errorKey: 'coverage.pre_onboarding.sleepio.password.error_length',
    satisfiedBy: (value) => value.length >= PASSWORD_MIN_LENGTH,
  },
  {
    id: 'lowercase',
    errorKey: 'coverage.pre_onboarding.sleepio.password.error_lowercase',
    satisfiedBy: (value) => /[a-z]/.test(value),
  },
  {
    id: 'uppercase',
    errorKey: 'coverage.pre_onboarding.sleepio.password.error_uppercase',
    satisfiedBy: (value) => /[A-Z]/.test(value),
  },
  {
    id: 'digit',
    errorKey: 'coverage.pre_onboarding.sleepio.password.error_digit',
    satisfiedBy: (value) => /[0-9]/.test(value),
  },
  {
    id: 'symbol',
    errorKey: 'coverage.pre_onboarding.sleepio.password.error_symbol',
    // Scan the symbol set rather than the value: the compile target is ES5, so
    // iterating a string with a spread is not available here.
    satisfiedBy: (value) => PASSWORD_SYMBOLS.split('').some((char) => value.indexOf(char) !== -1),
  },
] as const;

/** i18n key for the rule line shown before the member types. Interpolates `minLength`. */
export const PASSWORD_RULE_HINT_KEY = 'coverage.pre_onboarding.sleepio.password.rule';

/** Interpolation values every consumer of the rule copy must pass to `t()`. */
export const passwordRuleInterpolation = { minLength: PASSWORD_MIN_LENGTH };

/** Returns the first rule the value fails, or null when it satisfies all of them. */
export const findPasswordRuleViolation = (value: string): PasswordRule | null => (
  PASSWORD_RULES.find((rule) => !rule.satisfiedBy(value)) ?? null
);

export const isPasswordValid = (value: string): boolean => (
  findPasswordRuleViolation(value) === null
);

/** i18n key naming why the value is not acceptable, or null when it is. */
export const passwordErrorKey = (value: string): string | null => (
  findPasswordRuleViolation(value)?.errorKey ?? null
);
